import React, { Fragment, useContext, useEffect, useMemo, useState } from "react"
import moment from "moment"

import { db, funEU } from "../../../utils/firebase"
import { AlertsContext } from "../../../utils/providers/alerts"
import { AuthContext } from "../../../utils/providers/auth"

import Badge from "../../../components/ui/badge/badge"
import Button from "../../../components/ui/button/button"
import CreateClientPaymentLink from "../../../components/payments/create-client-payment-link/create-client-payment-link"
import ApplyCreditToBooking from "../../../components/payments/apply-credit-to-booking/apply-credit-to-booking"
import { PlusIcon, CopyIcon } from "../../../utils/svgs"
import { ChevronDown } from "lucide-react"

import styles from "./payments.module.scss"

const resendCallable = funEU.httpsCallable("resendClientPaymentLink")
const cancelCallable = funEU.httpsCallable("cancelClientPaymentLink")

const STATUS_META = {
	SUCCEEDED: { label: "Succeeded", type: "POSITIVE" },
	AWAITING: { label: "Awaiting payment", type: "AMBER" },
	CANCELLED: { label: "Cancelled", type: "" },
	APPLIED: { label: "Applied to booking", type: "INFO" },
}

const formatGbp = (value) =>
	Number(value || 0).toLocaleString("en-GB", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})

const tsToMoment = (ts) => {
	if (!ts) return null
	if (typeof ts.seconds === "number") return moment(ts.seconds, "X")
	if (ts._seconds) return moment(ts._seconds, "X")
	return null
}

/**
 * Payments tab on the client profile. Shows standalone (client-level)
 * payment_links and the resulting payments. Paid standalone payments can
 * be "applied to a booking" — moves them onto a specific booking so
 * `paid_by_client` increments via the existing trigger.
 */
export default function Payments({ clientID }) {
	const { user } = useContext(AuthContext)
	const { pushAlert } = useContext(AlertsContext)

	const [paymentLinks, setPaymentLinks] = useState([])
	const [payments, setPayments] = useState([])
	const [loadingLinks, setLoadingLinks] = useState(true)
	const [loadingPayments, setLoadingPayments] = useState(true)
	const [creating, setCreating] = useState(false)
	const [applyingCredit, setApplyingCredit] = useState(null)
	const [expandedId, setExpandedId] = useState(null)
	const [busyId, setBusyId] = useState(null)
	const [busyAction, setBusyAction] = useState(null)

	useEffect(() => {
		if (!clientID) return

		const unsubLinks = db
			.collection(`clients/${clientID}/payment_links`)
			.orderBy("sent_at", "desc")
			.onSnapshot((snap) => {
				setPaymentLinks(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
				setLoadingLinks(false)
			})

		const unsubPayments = db
			.collection(`clients/${clientID}/payments`)
			.orderBy("paid_date", "desc")
			.onSnapshot((snap) => {
				setPayments(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
				setLoadingPayments(false)
			})

		return () => {
			unsubLinks()
			unsubPayments()
		}
	}, [clientID])

	const rows = useMemo(() => {
		const merged = []

		payments.forEach((p) => {
			merged.push({
				id: `payment-${p.id}`,
				kind: "PAYMENT",
				rawId: p.id,
				date: tsToMoment(p.paid_date),
				payee: p.payee || "—",
				reference: p.reference || "—",
				status: p.claimed_at ? "APPLIED" : p.status || "SUCCEEDED",
				amount: Number(p.amount || 0),
				raw: p,
			})
		})

		paymentLinks.forEach((l) => {
			if (l.status === "PAID") return // its payment record represents it
			merged.push({
				id: `link-${l.id}`,
				kind: "LINK",
				rawId: l.id,
				date: tsToMoment(l.sent_at) || tsToMoment(l.last_sent_at),
				payee: l.payee || "—",
				reference: "Awaiting checkout",
				status: l.status === "CANCELLED" ? "CANCELLED" : "AWAITING",
				amount: Number(l.amount || 0),
				raw: l,
			})
		})

		merged.sort((a, b) => (b.date?.valueOf() || 0) - (a.date?.valueOf() || 0))
		return merged
	}, [payments, paymentLinks])

	const unappliedTotal = useMemo(
		() => payments.filter((p) => !p.claimed_at).reduce((sum, p) => sum + Number(p.amount || 0), 0),
		[payments],
	)

	const loading = loadingLinks || loadingPayments

	const handleCopy = async (url) => {
		try {
			await navigator.clipboard.writeText(url)
			pushAlert({ type: "SUCCESS", title: "Link copied" })
		} catch {
			pushAlert({ type: "ERROR", title: "Couldn't copy — copy it manually." })
		}
	}

	const handleResend = async (row) => {
		setBusyId(row.id)
		setBusyAction("resend")
		try {
			await resendCallable({ clientID, linkID: row.rawId })
			pushAlert({ type: "SUCCESS", title: "Payment link re-sent" })
		} catch (err) {
			pushAlert({ type: "ERROR", title: err?.message || "Could not resend." })
		} finally {
			setBusyId(null)
			setBusyAction(null)
		}
	}

	const handleCancel = async (row) => {
		setBusyId(row.id)
		setBusyAction("cancel")
		try {
			await cancelCallable({ clientID, linkID: row.rawId })
			pushAlert({ type: "SUCCESS", title: "Payment link cancelled" })
		} catch (err) {
			pushAlert({ type: "ERROR", title: err?.message || "Could not cancel." })
		} finally {
			setBusyId(null)
			setBusyAction(null)
		}
	}

	return (
		<div className={styles.wrap}>
			<div className={styles.actionBar}>
				<Button
					label="Create payment link"
					icon={<PlusIcon />}
					small={true}
					onClick={() => setCreating(true)}
				/>
			</div>

			<div className={styles.summary}>
				<span>
					Credit on account <strong>£{formatGbp(unappliedTotal)}</strong>
				</span>
				<span>
					{paymentLinks.length} link{paymentLinks.length === 1 ? "" : "s"} all-time
				</span>
				<span>
					{payments.length} standalone payment{payments.length === 1 ? "" : "s"}
				</span>
			</div>

			<p className={styles.title}>Standalone payment history</p>

			{loading && <p className={styles.empty}>Loading...</p>}

			{!loading && rows.length === 0 && (
				<p className={styles.empty}>No standalone payment links or payments yet. Create one to get started.</p>
			)}

			{!loading && rows.length > 0 && (
				<table className={styles.table}>
					<thead>
						<tr>
							<th>Date</th>
							<th>Payee</th>
							<th>Reference</th>
							<th>Status</th>
							<th style={{ textAlign: "right" }}>Amount</th>
							<th style={{ width: 22 }} />
						</tr>
					</thead>
					<tbody>
						{rows.map((row) => {
							const statusMeta = STATUS_META[row.status] || STATUS_META.SUCCEEDED
							const isExpanded = expandedId === row.id

							return (
								<Fragment key={row.id}>
									<tr
										className={[styles.row, isExpanded ? styles.rowExpanded : ""].join(" ")}
										onClick={() => setExpandedId(isExpanded ? null : row.id)}>
										<td>{row.date ? row.date.format("DD/MM/YYYY") : "—"}</td>
										<td>{row.payee}</td>
										<td className={styles.muted}>{row.reference}</td>
										<td>
											<Badge
												type={statusMeta.type}
												label={statusMeta.label}
											/>
										</td>
										<td className={styles.amount}>£{formatGbp(row.amount)}</td>
										<td className={styles.chevron}>
											<ChevronDown
												size={14}
												strokeWidth={1.75}
											/>
										</td>
									</tr>

									{isExpanded && (
										<tr className={styles.detailRow}>
											<td colSpan={6}>
												<RowDetail
													row={row}
													user={user}
													busy={busyId === row.id}
													busyAction={busyAction}
													onCopy={() => handleCopy(row.raw.stripe_payment_link_url)}
													onResend={() => handleResend(row)}
													onCancel={() => handleCancel(row)}
													onApply={() =>
														setApplyingCredit({
															paymentID: row.rawId,
															amount: row.amount,
															reference: row.reference,
														})
													}
												/>
											</td>
										</tr>
									)}
								</Fragment>
							)
						})}
					</tbody>
				</table>
			)}

			{creating && (
				<CreateClientPaymentLink
					close={() => setCreating(false)}
					onCreated={() => setCreating(false)}
				/>
			)}

			{applyingCredit && (
				<ApplyCreditToBooking
					clientID={clientID}
					paymentID={applyingCredit.paymentID}
					amount={applyingCredit.amount}
					reference={applyingCredit.reference}
					close={() => setApplyingCredit(null)}
				/>
			)}
		</div>
	)
}

function RowDetail({ row, busy, busyAction, onCopy, onResend, onCancel, onApply }) {
	if (row.kind === "LINK") {
		const link = row.raw
		const sentAt = tsToMoment(link.sent_at)
		const lastSent = tsToMoment(link.last_sent_at)
		const cancelledAt = tsToMoment(link.cancelled_at)

		return (
			<div className={styles.detailPanel}>
				<div className={styles.detailGrid}>
					<DetailField
						label="Recipient"
						value={link.recipient_email}
					/>
					<DetailField
						label="Sent by"
						value={link.sent_by_name}
					/>
					{sentAt && (
						<DetailField
							label="Sent at"
							value={sentAt.format("DD MMM YYYY [at] HH:mm")}
						/>
					)}
					{link.send_count > 1 && lastSent && (
						<DetailField
							label="Send count"
							value={`${link.send_count} (last ${lastSent.fromNow()})`}
						/>
					)}
					{link.note && (
						<DetailField
							label="Note"
							value={link.note}
							wide
						/>
					)}
					{link.stripe_payment_link_url && (
						<DetailField
							label="Stripe link"
							value={
								<a
									href={link.stripe_payment_link_url}
									target="_blank"
									rel="noopener noreferrer">
									{link.stripe_payment_link_url}
								</a>
							}
							wide
						/>
					)}
					{cancelledAt && (
						<DetailField
							label="Cancelled at"
							value={cancelledAt.format("DD MMM YYYY [at] HH:mm")}
						/>
					)}
				</div>

				{row.status === "AWAITING" && (
					<div className={styles.detailActions}>
						<Button
							label="Copy link"
							icon={<CopyIcon />}
							type="secondary"
							small={true}
							onClick={onCopy}
							disabled={busy}
						/>
						<Button
							label="Resend email"
							type="secondary"
							small={true}
							loading={busyAction === "resend"}
							loadingText="Sending..."
							disabled={busy && busyAction !== "resend"}
							onClick={onResend}
						/>
						<Button
							label="Cancel link"
							small={true}
							loading={busyAction === "cancel"}
							loadingText="Cancelling..."
							disabled={busy && busyAction !== "cancel"}
							onClick={onCancel}
						/>
					</div>
				)}
			</div>
		)
	}

	// PAYMENT row (standalone client payment)
	const payment = row.raw
	const canApply = !payment.claimed_at && Number(payment.amount || 0) > 0

	return (
		<div className={styles.detailPanel}>
			<div className={styles.detailGrid}>
				{payment.receipt_sent_to && (
					<DetailField
						label="Receipt sent to"
						value={payment.receipt_sent_to}
					/>
				)}
				{typeof payment.stripe_fee_amount === "number" && (
					<DetailField
						label="Stripe fees"
						value={`£${formatGbp(payment.stripe_fee_amount)}`}
					/>
				)}
				{typeof payment.stripe_net_amount === "number" && (
					<DetailField
						label="Net to GHD"
						value={`£${formatGbp(payment.stripe_net_amount)}`}
					/>
				)}
				{payment.stripe_charge_id && (
					<DetailField
						label="Charge ID"
						value={payment.stripe_charge_id}
						wide
					/>
				)}
				{payment.claimed_booking_id && (
					<DetailField
						label="Applied to booking"
						value={payment.claimed_booking_id}
						wide
					/>
				)}
			</div>

			{canApply && (
				<div className={styles.detailActions}>
					<Button
						label="Apply to a booking"
						small={true}
						onClick={onApply}
					/>
				</div>
			)}
		</div>
	)
}

function DetailField({ label, value, wide }) {
	return (
		<div className={[styles.detailField, wide ? styles.detailFieldWide : ""].join(" ")}>
			<p className={styles.detailLabel}>{label}</p>
			<p className={styles.detailValue}>{value}</p>
		</div>
	)
}
